import debounce from 'lodash/debounce';
import { SEARCH_DEBOUNCE_DELAY } from '@/store/baseModule/actions';

const ACTIONS_NATURE_ID = 'exp/N67';

const makeEMediaModule = ({
  eMediaService,
  sortChoices,
  customGetters = null,
  customMutations = null,
  customActions = null,
}) => {
  return {
    namespaced: true,
    state: {
      propertyId: null,
      propertyCode: null,
      actionId: null,
      interventionId: null,
      items: {
        isViewTable: true,
        isLoading: false,
        data: [],
        error: null,
        count: 0,
        sortChoices,
        sort: { by: [], ASC: [] },
        filters: {},
        pageIndex: 0,
        pageSize: 20,
      },
      categories: {
        data: [],
        error: null,
      },
      url: {},
    },
    getters: {
      ...customGetters,
      propertyId: (state) => state.propertyId,
      propertyCode: (state) => state.propertyCode,
      actionId: (state) => state.actionId,
      interventionId: (state) => state.interventionId,
      isViewTable: (state) => state.items.isViewTable,
      isLoading: (state) => state.items.isLoading,
      items: (state) => state.items.data,
      count: (state) => state.items.count,
      error: (state) => state.items.error,
      sort: (state) => state.items.sort,
      sortBy: (state) => state.items.sort.by,
      sortASC: (state) => state.items.sort.ASC,
      orderByText: (state) => {
        let res = '';
        state.items.sort.by.forEach((el, index) => {
          res += `${index > 0 ? ', ' : ''}${el} ${
            state.items.sort.ASC[index] ? 'asc' : 'desc'
          }`;
        });
        return res;
      },
      pageIndex: (state) => state.items.pageIndex,
      pageSize: (state) => state.items.pageSize,
      filters: (state) => state.items.filters,
      categories: (state) => state.categories.data,
      url: (state) => state.url,
      isFiltersDirty: (state) => {
        let result = 0;
        if (state.items.filters.category) result += 1;
        if (state.items.filters.nature) result += 1;
        if (state.items.filters.internal)
          result += state.items.filters.internal.length;
        return result;
      },
    },
    mutations: {
      ...customMutations,
      SET_PROPERTY_ID: (state, id) => {
        state.propertyId = id;
      },
      SET_PROPERTY_CODE: (state, code) => {
        state.propertyCode = code;
      },
      SET_ACTION_ID: (state, id) => {
        state.actionId = id;
      },
      SET_INTERVENTION_ID: (state, id) => {
        state.interventionId = id;
      },
      SET_VIEW_TABLE: (state, viewTable) => {
        state.items.isViewTable = viewTable;
      },
      SET_ITEMS_REQUEST: (state) => {
        state.items.data = [];
        state.items.count = 0;
        state.items.error = null;
        state.items.isLoading = true;
      },
      SET_ITEMS: (state, { items, count }) => {
        state.items.data = items;
        state.items.count = count;
        state.items.isLoading = false;
      },
      SET_ITEMS_ERROR: (state, error) => {
        state.items.error = error;
        state.items.isLoading = false;
      },
      SET_SORT_BY: (state, sort) => {
        state.items.sort.by = sort;
      },
      SET_SORT_ASC: (state, sort) => {
        state.items.sort.ASC = sort;
      },
      SET_FILTER_ATTRIBUTE: (state, { attribute, value }) => {
        state.items.filters = { ...state.items.filters, [attribute]: value };
      },
      CLEAR_FILTERS: (state) => {
        state.items.filters = {};
      },
      SET_ITEMS_PAGE: (state, pageIndex) => {
        state.items.pageIndex = pageIndex;
      },
      SET_ITEMS_PAGE_SIZE: (state, pageSize) => {
        state.items.pageSize = pageSize;
      },
      SET_CATEGORIES_REQUEST: (state) => {
        state.categories.data = [];
        state.categories.error = null;
      },
      SET_CATEGORIES: (state, data) => {
        state.categories.data = data;
      },
      SET_CATEGORIES_ERROR: (state, error) => {
        state.categories.error = error;
      },
      ADD_URL: (state, { url, uid }) => {
        state.url = { ...state.url, [uid]: url };
      },
      CLEAR_URL: (state) => {
        state.url = {};
      },
    },
    actions: {
      ...customActions,
      toogleViewTable({ commit, getters }) {
        commit('SET_VIEW_TABLE', !getters.isViewTable);
      },
      loadCategories({ commit }) {
        commit('SET_CATEGORIES_REQUEST');
        return eMediaService
          .getCategories()
          .then((result) => {
            commit('SET_CATEGORIES', result.categories);
          })
          .catch((error) => {
            commit('SET_CATEGORIES_ERROR', error);
          });
      },
      loadItems({ commit, getters }) {
        commit('SET_ITEMS_REQUEST');
        return eMediaService
          .getDocuments(getters)
          .then((result) => {
            commit('SET_ITEMS', {
              items: result.documents,
              count: result.count,
            });
          })
          .catch((error) => {
            commit(
              'SET_ITEMS_ERROR',
              error.response && error.response.data && error.response.data.value
                ? error.response.data.value.errors
                : error
            );
          });
      },
      loadItemsWithDelay: debounce(({ dispatch }) => {
        dispatch('loadItems');
      }, SEARCH_DEBOUNCE_DELAY),
      loadPropertyItems({ commit, dispatch }, { propertyId }) {
        commit('SET_ACTION_ID', null);
        commit('SET_PROPERTY_ID', propertyId);
        dispatch('setItemsPage', 0);
      },
      loadActionItems({ commit, dispatch }, { actionId, propertyId }) {
        commit('SET_ACTION_ID', actionId);
        commit('SET_PROPERTY_ID', propertyId);
        dispatch('setItemsPage', 0);
      },
      setItemsPage({ commit, dispatch }, pageIndex) {
        commit('SET_ITEMS_PAGE', pageIndex);
        dispatch('loadItems');
      },
      setItemsPageSize({ commit, dispatch }, pageSize) {
        commit('SET_ITEMS_PAGE_SIZE', pageSize);
        dispatch('loadItems');
      },
      addUrl({ commit }, { url, uid }) {
        commit('ADD_URL', { url, uid });
      },
      setItemsSortBy({ commit, dispatch }, payload) {
        commit('SET_SORT_BY', payload);
        commit('SET_ITEMS_PAGE', 0);
        dispatch('loadItemsWithDelay');
      },
      setOrderASC({ commit, dispatch }, payload) {
        commit('SET_SORT_ASC', payload);
        commit('SET_ITEMS_PAGE', 0);
        dispatch('loadItemsWithDelay');
      },
      setFilterAttribute({ commit, dispatch }, payload) {
        commit('SET_FILTER_ATTRIBUTE', payload);
        commit('SET_ITEMS_PAGE', 0);
        dispatch('loadItemsWithDelay');
      },
      clearFilters({ commit, dispatch }, payload) {
        commit('CLEAR_FILTERS', payload);
        commit('SET_ITEMS_PAGE', 0);
        dispatch('loadItemsWithDelay');
      },
    },
  };
};

export default makeEMediaModule;

export { ACTIONS_NATURE_ID };
